import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Tooltip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionIcon from "@mui/icons-material/Description";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import HelpIcon from "@mui/icons-material/Help";
import WarningIcon from "@mui/icons-material/Warning";
import EditIcon from "@mui/icons-material/Edit";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckIcon from "@mui/icons-material/Check";

import {
  aiCodingAssess,
  aiLabsExtract,
} from "../../../../utils/services/codingOpportunitiesAiClient";
import { flattenCodingOpsToText } from "../../IAsystem/mappers/codingOps.flatten";
import { flattenLabsToText } from "../../IAsystem/mappers/labs.flatten";
import useAuthUser from "../../../../hooks/useAuthUser";
import {
  getOverrides,
  saveOverride,
  removeOverride,
  savePatientStatus,
  getDecisionStatusId,
} from "../../utils/codingOpStorage";
import { CodingOpContext } from "../../../../context/CodingOpContext/CodingOpContext";
import EditDecisionDialog from "./EditDecisionDialog";
import FinishProcessDialog from "./FinishProcessDialog";
import { formatDateTime } from "../../../../utils/formatters/date";

// Convierte combinedNotesAnalysis (shape del front: findings/medications/notesSummaries)
// al shape que pide /ai/coding-assess en el body `notesSummary`:
// { summary, key_findings[], coding_relevance[], medications[] }.
// Medicamentos: 3ra fuente de evidencia (08_AI_BACKEND_ENDPOINTS.md §4).
const buildNotesSummaryForBackend = (combinedNotesAnalysis) => {
  if (!combinedNotesAnalysis) return null;
  const summaries = combinedNotesAnalysis.notesSummaries || [];
  const findings = combinedNotesAnalysis.findings || [];
  const medications = combinedNotesAnalysis.medications || [];
  return {
    summary: summaries
      .map((n) => n.summary)
      .filter(Boolean)
      .join("\n\n"),
    key_findings: findings.map((f) => ({
      condition: f.condition,
      evidence: f.evidence,
      date: f.sourceNoteDate || null,
      source_note_id: f.sourceNoteId || null,
      confidence: typeof f.confidence === "number" ? f.confidence : 0.8,
      negated: !!f.negated,
    })),
    coding_relevance: [],
    medications: medications.map((m) => ({
      medication: m.medication,
      active: m.active !== false,
      treats: m.treats || "unknown",
      icd10_hint: m.icd10_hint || null,
      // El combiner de IAAssistTab añade sourceNoteId (camelCase); aquí
      // reconvertimos al snake_case que espera el backend.
      source_note_id: m.source_note_id || m.sourceNoteId || null,
      date: m.date || null,
      confidence: typeof m.confidence === "number" ? m.confidence : null,
    })),
  };
};

// Traduce la respuesta del backend al shape que consumen los componentes de
// este archivo (y FinishProcessDialog): overall_summary + opportunities con
// code/description/codingOpportunityId/supporting_evidence.impact.
const adaptCodingAssessResponse = (response) => {
  if (!response) return null;
  return {
    ...response,
    overall_summary: response.patient_summary || response.overall_summary || "",
    opportunities: (response.opportunities || []).map((op) => ({
      ...op,
      code: op.diagnosisCode || op.code || "",
      description: op.hccDescription || op.description || "",
      codingOpportunityId:
        Number(op.id) && !Number.isNaN(Number(op.id))
          ? Number(op.id)
          : op.codingOpportunityId || op.id,
      supporting_evidence: (op.supporting_evidence || []).map((ev) => ({
        ...ev,
        // Backend no envía `impact`; por defecto positivo (el prompt solo
        // devuelve evidencia de apoyo; las contradicciones viven en
        // rationale / missing_info).
        impact: ev.impact || "positive",
        source_note_type:
          ev.source_note_type ||
          (ev.type === "note"
            ? "Clinical Note"
            : ev.type === "lab"
            ? "Lab Result"
            : null),
        source_note_date: ev.source_note_date || ev.date || null,
      })),
    })),
  };
};

const scoreColor = (v) => {
  if (v === null || v === undefined) return "default";
  if (v >= 0.85) return "success";
  if (v >= 0.65) return "warning";
  return "default";
};

const decisionIcon = (d) => {
  const x = String(d || "").toLowerCase();
  if (x.includes("approve") || x.includes("accept")) return <ThumbUpIcon fontSize="small" />;
  if (x.includes("reject")) return <ThumbDownIcon fontSize="small" />;
  if (x.includes("current") || x.includes("acute") || x.includes("off label")) return <CheckCircleIcon fontSize="small" />;
  return <HelpIcon fontSize="small" />;
};

const decisionColor = (d) => {
  const x = String(d || "").toLowerCase();
  if (x.includes("approve") || x.includes("accept")) return "success";
  if (x.includes("reject") || x.includes("non-existent")) return "error";
  if (x.includes("review") || x.includes("insufficient") || x.includes("off label")) return "warning";
  if (x.includes("current") || x.includes("acute")) return "info";
  return "default";
};

export default function CodingOppsAnalysisStep({
  patient,
  codingOpportunities,
  combinedNotesAnalysis,
  labs,
  codingOppsAnalysis,
  setCodingOppsAnalysis,
  disabled,
  advancedMdPatientId,
}) {
  const navigate = useNavigate();
  const user = useAuthUser();
  const { updateCodingOpportunityStatus, addCodingOpComment } = useContext(CodingOpContext);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  // Cache del resultado de /ai/labs-extract para no volver a llamarlo si el
  // usuario re-aprieta "Analyze" en la misma sesión del paciente. Se pierde
  // al desmontar, que es lo que queremos (labs pueden llegar nuevos después).
  const [labsAnalysis, setLabsAnalysis] = useState(null);
  const [detailDialog, setDetailDialog] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  // State for doctor overrides
  const [overrides, setOverrides] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOp, setEditingOp] = useState(null);

  // State for finish process
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const cipId = patient?.cipId;
  const opsCount = codingOpportunities?.length || 0;
  const analyzedOps = codingOppsAnalysis?.opportunities || [];

  // Load overrides from localStorage on mount
  useEffect(() => {
    if (cipId) {
      const savedOverrides = getOverrides(cipId);
      setOverrides(savedOverrides);
    }
  }, [cipId]);

  // Handle opening edit dialog
  const openEditDialog = (op) => {
    setEditingOp(op);
    setEditDialogOpen(true);
  };

  // Handle saving override (calls backend API)
  const handleSaveOverride = async (overrideData) => {
    if (!cipId || !editingOp?.id) return;

    const fullOverrideData = {
      ...overrideData,
      overriddenBy: user?.username || user?.email || "unknown",
    };

    // Get the status ID for the decision
    const statusId = getDecisionStatusId(overrideData.decision);

    try {
      // Call backend to update status
      if (statusId !== null) {
        const result = await updateCodingOpportunityStatus(
          editingOp.codingOpportunityId || editingOp.id,
          statusId
        );

        if (result) {
          // Save comment to backend if provided
          if (overrideData.comment && overrideData.comment.trim()) {
            try {
              await addCodingOpComment(
                editingOp.codingOpportunityId || editingOp.id,
                overrideData.comment,
                user?.rol || "PCP"
              );
            } catch (commentErr) {
              console.error("Error saving comment:", commentErr);
              // Continue even if comment fails - status was saved
            }
          }

          // Save locally for UI consistency
          saveOverride(cipId, editingOp.id, fullOverrideData);
          setOverrides((prev) => ({
            ...prev,
            [editingOp.id]: fullOverrideData,
          }));

          setSnackbar({
            open: true,
            message: `Decision saved successfully (${result.rowsAffected} row(s) updated)`,
            severity: "success",
          });
        } else {
          setSnackbar({ open: true, message: "Error saving decision to server", severity: "error" });
        }
      } else {
        // Fallback: save locally only if no status ID mapping
        saveOverride(cipId, editingOp.id, fullOverrideData);
        setOverrides((prev) => ({
          ...prev,
          [editingOp.id]: fullOverrideData,
        }));
        setSnackbar({ open: true, message: "Decision saved locally", severity: "warning" });
      }
    } catch (err) {
      console.error("Error saving override:", err);
      setSnackbar({ open: true, message: "Error saving decision", severity: "error" });
    }

    setEditDialogOpen(false);
    setEditingOp(null);
  };

  // Handle removing override
  const handleRemoveOverride = () => {
    if (!cipId || !editingOp?.id) return;

    removeOverride(cipId, editingOp.id);
    setOverrides((prev) => {
      const updated = { ...prev };
      delete updated[editingOp.id];
      return updated;
    });

    setEditDialogOpen(false);
    setEditingOp(null);
    setSnackbar({ open: true, message: "Override removed", severity: "info" });
  };

  // Handle quick approve - accepts the AI decision and saves it to backend
  const handleQuickApprove = async (op) => {
    if (!op?.id) return;

    const aiDecision = op.decision; // The AI's suggested decision
    const statusId = getDecisionStatusId(aiDecision);

    if (statusId === null) {
      setSnackbar({ open: true, message: "Cannot map AI decision to status ID", severity: "error" });
      return;
    }

    setApprovingId(op.id);

    try {
      // Call backend to update status with the AI's decision
      const result = await updateCodingOpportunityStatus(
        op.codingOpportunityId || op.id,
        statusId
      );

      if (result) {
        // Save locally as "approved" (accepted AI decision)
        const approveOverride = {
          decision: aiDecision, // Keep the AI's decision
          comment: "",
          originalDecision: aiDecision,
          isApproved: true, // Flag to indicate this was approved, not modified
          overriddenBy: user?.username || user?.email || "unknown",
        };

        saveOverride(cipId, op.id, approveOverride);
        setOverrides((prev) => ({
          ...prev,
          [op.id]: approveOverride,
        }));

        setSnackbar({
          open: true,
          message: `AI decision "${aiDecision}" approved (${result.rowsAffected} row(s) updated)`,
          severity: "success",
        });
      } else {
        setSnackbar({ open: true, message: "Error approving coding opportunity", severity: "error" });
      }
    } catch (err) {
      console.error("Error approving:", err);
      setSnackbar({ open: true, message: "Error approving coding opportunity", severity: "error" });
    } finally {
      setApprovingId(null);
    }
  };

  // Handle finish process
  const handleFinishProcess = ({ status, comment }) => {
    if (!cipId) return;

    savePatientStatus(cipId, status, comment, user?.username || user?.email || "unknown");
    setFinishDialogOpen(false);
    setSnackbar({ open: true, message: `Patient status set to "${status}"`, severity: "success" });

    // Navigate to worklist after a short delay to show the snackbar
    setTimeout(() => {
      navigate("/coding-opportunities", { state: { refresh: true } });
    }, 1500);
  };

  // Get final decision for an opportunity (override or AI)
  const getFinalDecision = (op) => {
    const override = overrides[op.id];
    if (override) {
      return {
        decision: override.decision,
        isOverridden: true,
        isApproved: override.isApproved || false,
      };
    }
    return { decision: op.decision, isOverridden: false, isApproved: false };
  };

  // Check if all coding opportunities have been processed (approved or modified)
  const allOpportunitiesProcessed = React.useMemo(() => {
    if (!analyzedOps || analyzedOps.length === 0) return false;

    // Every opportunity must have an override (either approved or modified)
    return analyzedOps.every((op) => {
      const override = overrides[op.id];
      return override !== undefined && override !== null;
    });
  }, [analyzedOps, overrides]);

  // Count of processed opportunities
  const processedCount = React.useMemo(() => {
    if (!analyzedOps) return 0;
    return analyzedOps.filter((op) => overrides[op.id]).length;
  }, [analyzedOps, overrides]);

  // Analyze coding opportunities vía backend proxy.
  // Encadena labs-extract → coding-assess (doc 10 §5.3).
  const runAnalyzeCodingOpps = async () => {
    try {
      setError(null);
      setRunning(true);

      // HIPAA: no loguear nombre del paciente ni contenido clínico — 45 CFR 164.530(c)
      console.log("=== CODING OPPORTUNITIES ANALYSIS ===");
      console.log("Coding Opportunities:", codingOpportunities?.length);
      console.log("Total Findings:", combinedNotesAnalysis?.findings?.length);
      console.log("Labs available:", labs?.length || 0);

      // HIPAA 45 CFR 164.514 (Safe Harbor): solo age/gender/memberType.
      // NO enviar dateOfBirth (identificador #3) al pipeline de IA.
      const patientWhitelist = {
        age: patient?.age,
        gender: patient?.gender,
        memberType: patient?.memberType,
      };
      const codingOpsText = flattenCodingOpsToText(codingOpportunities || []);

      // ────────────────────────────────────────────────────────────────
      // PASO 1 — labs-extract (automático, solo si hay labs y no cacheado)
      // ────────────────────────────────────────────────────────────────
      let labsSummaryForBackend = labsAnalysis;

      if (!labsSummaryForBackend && Array.isArray(labs) && labs.length > 0) {
        try {
          const labsText = flattenLabsToText(labs);

          labsSummaryForBackend = await aiLabsExtract({
            cipId: patient?.cipId,
            patient: patientWhitelist,
            labsText,
          });

          setLabsAnalysis(labsSummaryForBackend); // cache local para la sesión
        } catch (err) {
          console.warn(
            "[labs-extract] falló, degradando a labsSummary=null",
            err
          );
          labsSummaryForBackend = null; // NO abortar
        }
      }

      // ────────────────────────────────────────────────────────────────
      // PASO 2 — coding-assess (siempre)
      // ────────────────────────────────────────────────────────────────
      const codingJson = await aiCodingAssess({
        cipId: patient?.cipId,
        patient: patientWhitelist,
        codingOpsText,
        notesSummary: buildNotesSummaryForBackend(combinedNotesAnalysis),
        labsSummary: labsSummaryForBackend, // objeto o null
      });

      if (!codingJson) throw new Error("AI returned empty response. Try again.");
      setCodingOppsAnalysis(adaptCodingAssessResponse(codingJson));
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  // Link a Clinical Note
  const linkResultToClinicalNote = () => {
    if (!codingOppsAnalysis) return;

    const payload = {
      source: "coding-opportunities-ai",
      createdAt: new Date().toISOString(),
      patient: {
        memberFullName: patient?.memberFullName ?? null,
        dateOfBirth: patient?.dateOfBirth ?? null,
        gender: patient?.gender ?? null,
        cipId: patient?.cipId ?? null,
        advancedMdPatientId: advancedMdPatientId ?? null,
      },
      meta: {
        model: "gpt-4o-mini",
        opsCount,
        notesAnalyzed: combinedNotesAnalysis?.totalNotesAnalyzed || 0,
        findingsCount: combinedNotesAnalysis?.findings?.length || 0,
        labsCount: labs?.length || 0,
      },
      combinedNotesAnalysis,
      codingOppsAnalysis,
    };

    navigate("/coding-opportunities/clinical-notes/ai", { state: payload });
  };

  if (disabled) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: "center", bgcolor: "grey.100" }}>
        <WarningIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Complete Step 1 to enable Coding Opportunities analysis.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Main button */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
          onClick={runAnalyzeCodingOpps}
          disabled={running || opsCount === 0}
        >
          {running ? "Analyzing..." : `Analyze ${opsCount} Coding Opportunities`}
        </Button>

        {codingOppsAnalysis && (
          <>
            <Chip icon={<CheckCircleIcon />} label="Analysis Complete" color="success" />
            <Chip
              label={`${processedCount}/${analyzedOps.length} processed`}
              color={allOpportunitiesProcessed ? "success" : "warning"}
              variant="outlined"
            />
            {/* Link to Clinical Note button - hidden for now */}
            {/* <Button
              variant="outlined"
              color="secondary"
              startIcon={<LinkIcon />}
              onClick={linkResultToClinicalNote}
            >
              Link to Clinical Note
            </Button> */}
            <Tooltip
              title={
                !allOpportunitiesProcessed
                  ? `You must approve or modify all ${analyzedOps.length} coding opportunities before finishing`
                  : "Complete the process"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DoneAllIcon />}
                  onClick={() => setFinishDialogOpen(true)}
                  disabled={!allOpportunitiesProcessed}
                >
                  Finish Process
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Progress indicator */}
      {codingOppsAnalysis && analyzedOps.length > 0 && (
        <Alert
          severity={allOpportunitiesProcessed ? "success" : "warning"}
          sx={{ mb: 2 }}
        >
          {allOpportunitiesProcessed ? (
            <>All {analyzedOps.length} coding opportunities have been processed. You can now finish the process.</>
          ) : (
            <>
              {processedCount} of {analyzedOps.length} coding opportunities processed.
              Please approve or modify the remaining {analyzedOps.length - processedCount} to enable "Finish Process".
            </>
          )}
        </Alert>
      )}

      {/* Show overall summary */}
      {codingOppsAnalysis && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "primary.50" }}>
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            Overall Summary
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {codingOppsAnalysis?.overall_summary || "No summary available."}
          </Typography>
        </Paper>
      )}

      {/* Results list by coding opportunity */}
      {analyzedOps.length > 0 && (
        <Box>
          <Typography fontWeight={700} sx={{ mb: 2 }}>
            Results by Coding Opportunity
          </Typography>

          {analyzedOps.map((op, idx) => {
            const { decision: finalDecision, isOverridden, isApproved } = getFinalDecision(op);
            const override = overrides[op.id];
            const isProcessed = override !== undefined && override !== null;

            return (
            <Accordion key={op.id || idx} defaultExpanded={idx < 3}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                  {/* Final decision chip */}
                  <Chip
                    icon={decisionIcon(finalDecision)}
                    label={finalDecision || "—"}
                    color={decisionColor(finalDecision)}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography fontWeight={700}>
                    {op.code || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7, flex: 1 }}>
                    {op.description?.slice(0, 60)}...
                  </Typography>

                  {/* Status indicator: Approved, Modified, or Pending */}
                  {isProcessed ? (
                    <Tooltip title={override?.comment || (isApproved ? "AI decision approved" : "Modified by doctor")}>
                      <Chip
                        size="small"
                        icon={isApproved ? <CheckCircleIcon /> : <EditIcon />}
                        label={isApproved ? "Approved" : "Modified"}
                        color={isApproved ? "success" : "secondary"}
                        variant="outlined"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      label="Pending"
                      color="warning"
                      variant="outlined"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  <Chip
                    size="small"
                    label={`${Math.round((Number(op.confidence || 0) * 100))}%`}
                    color={scoreColor(op.confidence)}
                    variant="outlined"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {/* Action buttons */}
                  <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                    {/* Quick Approve button - visible only when not yet processed */}
                    {!isProcessed && (
                      <Tooltip title="Accept AI decision and save">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={
                            approvingId === op.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CheckIcon />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickApprove(op);
                          }}
                          disabled={approvingId === op.id}
                          sx={{ minWidth: 120 }}
                        >
                          Accept AI
                        </Button>
                      </Tooltip>
                    )}

                    {/* Modify button - always available to change decision */}
                    <Tooltip title={isProcessed ? "Change decision" : "Override AI decision"}>
                      <Button
                        size="small"
                        variant={isProcessed ? "text" : "outlined"}
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(op);
                        }}
                      >
                        {isProcessed ? "Change" : "Modify"}
                      </Button>
                    </Tooltip>
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {/* Status Section - Approved or Modified */}
                  {isProcessed && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        bgcolor: isApproved ? "success.50" : "secondary.50",
                        borderColor: isApproved ? "success.main" : "secondary.main",
                        borderWidth: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        {isApproved ? (
                          <>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography fontWeight={700} color="success.main">
                              AI Decision Approved
                            </Typography>
                          </>
                        ) : (
                          <>
                            <EditIcon color="secondary" fontSize="small" />
                            <Typography fontWeight={700} color="secondary.main">
                              Doctor Override
                            </Typography>
                          </>
                        )}
                      </Stack>
                      {isApproved ? (
                        <Chip
                          size="small"
                          label={`Decision: ${override?.decision}`}
                          color="success"
                        />
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={`Original: ${op.decision}`}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={`New: ${override?.decision}`}
                            color="secondary"
                          />
                        </Stack>
                      )}
                      {override?.comment && (
                        <Typography variant="body2" sx={{ fontStyle: "italic", mt: 1 }}>
                          "{override.comment}"
                        </Typography>
                      )}
                      {override?.overriddenBy && (
                        <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mt: 1 }}>
                          {isApproved ? "Approved" : "Modified"} by {override.overriddenBy} on{" "}
                          {formatDateTime(override.overriddenAt)}
                        </Typography>
                      )}
                    </Paper>
                  )}

                  {/* Rationale */}
                  <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                    AI Rationale
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.85 }}>
                    {op.rationale || "No rationale provided."}
                  </Typography>

                  {/* Supporting Evidence with note references */}
                  {op.supporting_evidence?.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography fontWeight={700} sx={{ mb: 1 }}>
                        <DescriptionIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                        Clinical Notes Referenced
                      </Typography>
                      <Stack spacing={1}>
                        {op.supporting_evidence.map((ev, evIdx) => (
                          <Paper
                            key={evIdx}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              borderLeft: 4,
                              borderLeftColor: ev.impact === "positive" ? "success.main" : "error.main",
                            }}
                          >
                            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 0.5 }}>
                              <Chip
                                size="small"
                                icon={ev.impact === "positive" ? <ThumbUpIcon /> : <ThumbDownIcon />}
                                label={ev.impact === "positive" ? "Supports" : "Contradicts"}
                                color={ev.impact === "positive" ? "success" : "error"}
                                variant="outlined"
                              />
                              {ev.finding_condition && (
                                <Chip size="small" label={ev.finding_condition} color="primary" variant="outlined" />
                              )}
                              {ev.source_note_type && (
                                <Chip size="small" label={ev.source_note_type} color="info" variant="outlined" />
                              )}
                              {ev.source_note_date && (
                                <Chip size="small" label={ev.source_note_date} variant="outlined" />
                              )}
                            </Stack>
                            <Typography variant="body2" sx={{ fontStyle: "italic", opacity: 0.85 }}>
                              "{ev.quote}"
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* Missing Info */}
                  {op.missing_info?.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography fontWeight={700} sx={{ mb: 1 }}>
                        Missing Information
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {op.missing_info.map((info, infoIdx) => (
                          <Chip key={infoIdx} size="small" label={info} color="warning" variant="outlined" />
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* Risk Flags */}
                  {op.risk_flags?.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography fontWeight={700} sx={{ mb: 1 }}>
                        Risk Flags
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {op.risk_flags.map((flag, flagIdx) => (
                          <Chip key={flagIdx} size="small" label={flag} color="error" variant="outlined" />
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* View full details */}
                  <Box sx={{ mt: 2, textAlign: "right" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDetailDialog(op)}
                    >
                      View Full Details
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            );
          })}
        </Box>
      )}

      {/* Full detail dialog */}
      <Dialog
        open={!!detailDialog}
        onClose={() => setDetailDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{detailDialog?.code || "Coding Opportunity"}</span>
            {detailDialog?.decision && (
              <Chip
                icon={decisionIcon(detailDialog.decision)}
                label={detailDialog.decision}
                color={decisionColor(detailDialog.decision)}
                size="small"
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailDialog && (
            <Box>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {detailDialog.description || "—"}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Confidence
              </Typography>
              <Chip
                label={`${Math.round((Number(detailDialog.confidence || 0) * 100))}%`}
                color={scoreColor(detailDialog.confidence)}
              />

              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Rationale
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                {detailDialog.rationale || "—"}
              </Typography>

              {detailDialog.supporting_evidence?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    All Supporting Evidence
                  </Typography>
                  <Stack spacing={1}>
                    {detailDialog.supporting_evidence.map((ev, idx) => (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderLeft: 4,
                          borderLeftColor: ev.impact === "positive" ? "success.main" : "error.main",
                        }}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={ev.impact === "positive" ? "Supports" : "Contradicts"}
                            color={ev.impact === "positive" ? "success" : "error"}
                          />
                          {ev.finding_condition && (
                            <Chip size="small" label={ev.finding_condition} color="primary" />
                          )}
                          <Chip size="small" label={ev.source_note_type || "Note"} variant="outlined" />
                          <Chip size="small" label={ev.source_note_date || "—"} variant="outlined" />
                        </Stack>
                        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                          "{ev.quote}"
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}

              {detailDialog.missing_info?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Missing Information
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {detailDialog.missing_info.map((m, i) => (
                      <Chip key={i} label={m} color="warning" variant="outlined" />
                    ))}
                  </Stack>
                </>
              )}

              {detailDialog.risk_flags?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Risk Flags
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {detailDialog.risk_flags.map((f, i) => (
                      <Chip key={i} label={f} color="error" variant="outlined" />
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Decision Dialog */}
      <EditDecisionDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingOp(null);
        }}
        opportunity={editingOp}
        currentOverride={editingOp ? overrides[editingOp.id] : null}
        onSave={handleSaveOverride}
        onRemoveOverride={handleRemoveOverride}
      />

      {/* Finish Process Dialog */}
      <FinishProcessDialog
        open={finishDialogOpen}
        onClose={() => setFinishDialogOpen(false)}
        opportunities={analyzedOps}
        overrides={overrides}
        onConfirm={handleFinishProcess}
        patient={patient}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
